(() => {
  const variantMapCache = new Map();
  let syncScheduled = false;
  const noSpacerLabels = new Set([
    'sin espaciadores',
    'no spacers',
    'sans entretoises',
    'ohne distanzringe',
    'senza distanziali'
  ]);

  const normalizeMeasurement = (value) => String(value || '').replace(/\D+/g, '');
  const isNoSpacerOption = (value) => noSpacerLabels.has(String(value || '').trim().toLowerCase());

  const fetchProductVariants = async (handle) => {
    const variants = [];
    let optionNames = [];
    const routeRoot = window.Shopify?.routes?.root || '/';

    for (let page = 1; page <= 5; page += 1) {
      const productUrl = `${routeRoot}products/${encodeURIComponent(handle)}.js?limit=250&page=${page}`;
      const response = await fetch(productUrl, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Unable to load ${productUrl}`);

      const product = await response.json();
      if (!optionNames.length) optionNames = (product.options || []).map((option) => String(option.name || option).toLowerCase());
      const pageVariants = Array.isArray(product.variants) ? product.variants : [];
      variants.push(...pageVariants);
      if (pageVariants.length < 250) break;
    }

    return { variants, optionNames };
  };

  const getNoSpacerVariantMap = (handle) => {
    if (variantMapCache.has(handle)) return variantMapCache.get(handle);

    const request = fetchProductVariants(handle)
      .then(({ variants, optionNames }) => {
        const variantMap = new Map();
        const widthIndex = optionNames.findIndex((name) => /width|anchura|ancho|largeur|breite|larghezza/.test(name));
        const stemIndex = optionNames.findIndex((name) => /stem|potencia|potence|vorbau|attacco/.test(name));
        variants.forEach((variant) => {
          const raw = Array.isArray(variant.options) ? variant.options : [];
          const options = [variant.option1 || raw[0], variant.option2 || raw[1], variant.option3 || raw[2]];
          if (variant.available === false || !isNoSpacerOption(options[2])) return;
          let width, stem;
          if (widthIndex >= 0 && stemIndex >= 0 && widthIndex !== stemIndex) {
            width = options[widthIndex]; stem = options[stemIndex];
          } else {
            const a = Number(normalizeMeasurement(options[0])), b = Number(normalizeMeasurement(options[1]));
            if (a >= 250 && b >= 30 && b <= 200) { width = options[0]; stem = options[1]; }
            else if (b >= 250 && a >= 30 && a <= 200) { width = options[1]; stem = options[0]; }
            else return;
          }

          variantMap.set(
            `${normalizeMeasurement(width)}|${normalizeMeasurement(stem)}`,
            String(variant.id)
          );
        });
        return variantMap;
      })
      .catch((error) => {
        variantMapCache.delete(handle);
        throw error;
      });

    variantMapCache.set(handle, request);
    return request;
  };

  const findProductForm = (picker) => {
    const root = picker.closest('.js-product')
      || picker.closest('.js-quickbuy')
      || picker.closest('.shopify-section')
      || document;
    return root.querySelector('.js-product-form')
      || root.querySelector('form[action*="/cart/add"]');
  };

  const getVariantInput = (form) => {
    let input = form.querySelector('[data-hyper2-upsell-variant]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.dataset.hyper2UpsellVariant = '';
      form.appendChild(input);
    }
    return input;
  };

  // Width and stem are button groups (snippets/raceit-property-picker.liquid):
  // the field is a fieldset, so its value lives on the checked radio inside.
  const getPropertyValue = (field) => {
    if (!field || field.disabled) return '';
    return field.querySelector('[data-raceit-property-option]:checked')?.value || field.value || '';
  };

  const getActivePropertyGroup = (picker) => Array.from(
    picker.querySelectorAll('[data-winspace-handlebar-properties]')
  ).find((group) => !group.hidden && !group.querySelector('[data-winspace-handlebar-selector] [data-hyper2-base-choice]:checked'));

  const syncPicker = async (picker) => {
    const form = findProductForm(picker);
    if (!form) return;

    const variantInput = getVariantInput(form);
    const selectedOption = picker.querySelector('[data-hyper2-integrated-option]:checked');
    if (!selectedOption) {
      variantInput.value = '';
      delete variantInput.dataset.loading;
      return;
    }

    const propertyGroup = getActivePropertyGroup(picker);
    const width = getPropertyValue(propertyGroup?.querySelector('[data-winspace-property-width]'));
    const stem = getPropertyValue(propertyGroup?.querySelector('[data-winspace-property-stem]'));
    const handle = selectedOption.dataset.hyper2ProductHandle;
    variantInput.value = '';

    if (!handle || !width || !stem) return;

    variantInput.dataset.loading = 'true';
    try {
      const variantMap = await getNoSpacerVariantMap(handle);
      if (!selectedOption.checked) return;
      variantInput.value = variantMap.get(
        `${normalizeMeasurement(width)}|${normalizeMeasurement(stem)}`
      ) || '';
    } catch (error) {
      console.error('Hyper2.0 no-spacer variants could not be loaded:', error);
    } finally {
      delete variantInput.dataset.loading;
    }
  };

  const syncAll = (scope = document) => {
    scope.querySelectorAll('variant-picker').forEach((picker) => {
      picker.querySelectorAll('[data-hyper2-integrated-option]').forEach((option) => {
        const handle = option.dataset.hyper2ProductHandle;
        if (handle) getNoSpacerVariantMap(handle).catch(() => {});
      });
      syncPicker(picker);
    });
  };

  const scheduleSyncAll = () => {
    if (syncScheduled) return;
    syncScheduled = true;
    window.requestAnimationFrame(() => {
      syncScheduled = false;
      syncAll();
    });
  };

  document.addEventListener('change', (event) => {
    if (!event.target.closest?.([
      '[data-hyper2-integrated-option]',
      '[data-hyper2-base-choice]',
      '[data-winspace-property-width]',
      '[data-winspace-property-stem]'
    ].join(', '))) return;

    const picker = event.target.closest('variant-picker');
    if (!picker) return;

    if (event.target.matches('[data-hyper2-integrated-option], [data-hyper2-base-choice]')) {
      const selected = event.target.matches('[data-hyper2-integrated-option]');
      picker.setIntegratedHyper2State?.(selected);
      picker.syncIntegratedHyper2Classes?.();
      picker.updateWinspaceHandlebarProperties?.();
    }

    window.setTimeout(() => syncPicker(picker), 0);
  }, true);

  // The width and stem buttons are selected by variant-picker.js, which does
  // not always fire `change`, so a click on either group also resyncs.
  document.addEventListener('click', (event) => {
    const propertyField = event.target.closest?.('[data-winspace-property-width], [data-winspace-property-stem]');
    const picker = propertyField?.closest('variant-picker');
    if (picker) window.setTimeout(() => syncPicker(picker), 0);
  }, true);

  document.addEventListener('click', (event) => {
    const directChoice = event.target.closest?.('[data-hyper2-integrated-option], [data-hyper2-base-choice]');
    const choiceLabel = event.target.closest?.('label[for]');
    const labelledChoice = choiceLabel ? document.getElementById(choiceLabel.htmlFor) : null;
    const choice = directChoice
      || (labelledChoice?.matches?.('[data-hyper2-integrated-option], [data-hyper2-base-choice]')
        ? labelledChoice
        : null);
    if (!choice) return;

    window.setTimeout(() => {
      const picker = choice.closest('variant-picker');
      if (!picker) return;
      picker.handleVariantChange?.({ target: choice, detail: { noFocus: true } });
      syncPicker(picker);
    }, 0);
  }, true);

  document.addEventListener('on:hyper2:change', (event) => {
    const picker = event.target.closest?.('variant-picker') || event.target;
    if (picker?.matches?.('variant-picker')) window.setTimeout(() => syncPicker(picker), 0);
  });

  // product-form.js calls this on submit when the hidden variant is still
  // empty, so a click that lands before the async lookup finishes still adds
  // the right Hyper2.0 instead of reporting missing sizes.
  window.raceitHyper2Upsell = {
    async resolve(form) {
      const root = form.closest('.js-product') || form.closest('.js-quickbuy') || form.closest('.shopify-section') || document;
      const pickers = Array.from(root.querySelectorAll('variant-picker'))
        .filter((picker) => picker.querySelector('[data-hyper2-integrated-option]:checked'));
      await Promise.all(pickers.map((picker) => syncPicker(picker)));
      return form.querySelector('[data-hyper2-upsell-variant]')?.value || '';
    }
  };

  document.addEventListener('DOMContentLoaded', () => syncAll());
  document.addEventListener('shopify:section:load', (event) => syncAll(event.target));

  const observer = new MutationObserver(scheduleSyncAll);
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
